// Self-built chat manager (replaces CometChat). Talks to our GraphQL API via the
// shared Apollo client and uses HTTP polling instead of a realtime socket.
//
// The public surface (static getInstance/initialize/login/logout, instance
// connect/subscribeToGroup/fetchMessages/sendTextMessage/sendMediaMessage/
// markAsRead/fetchAllUnreadMessageCount, and the Conversation/Message/User
// model shapes) is preserved so ChatContext.jsx + pages are unchanged.
//
// Conventions kept from the old implementation:
//  - `groupId` = conversation identifier (now our conversation.uid)
//  - Message.type is a lowercase string: 'text' | 'image' | 'video' | 'file' | 'custom' | 'system'
//  - Message.sentAt is a UNIX-seconds number (UI does sentAt * 1000)

import apollo from './apollo';
import {
  getConversationsQuery,
  getMessagesQuery,
  getNewMessagesQuery,
  getChatUnreadCountQuery,
  sendMessageMutation,
  startConversationMutation,
  markConversationReadMutation,
  chatHeartbeatMutation,
  uploadFilesMutation,
} from '@witness/graphql';

// Message types as returned by our API (uppercase enum) -> UI lowercase strings.
const TYPE_MAP = {
  TEXT: 'text',
  IMAGE: 'image',
  VIDEO: 'video',
  FILE: 'file',
  CUSTOM: 'custom',
  SYSTEM: 'system',
};

const MESSAGE_LIMIT = 30;
const CONVERSATION_PAGE_SIZE = 20; // conversation list page (infinite scroll)
const FAST_POLL_MS = 3000; // open conversation
const UNREAD_POLL_MS = 3000; // conversation list + unread (drives the unread badge)
const SLOW_POLL_MS = 20000; // presence heartbeat

const toUnixSeconds = (iso) => Math.floor(new Date(iso).getTime() / 1000);

const newClientMsgId = () => {
  if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

class ChatManager {
  static instance = null;
  static initialized = null;
  static authorized = null;
  static myUserId = null;

  constructor() {
    // Listeners (set by ChatContext)
    this.onMessage = null;
    this.onConversationUpdate = null;
    this.onConversationAdd = null;

    // Subscribed (open) conversation state
    this.subscribedGroupId = null;
    this.oldestSeq = null; // for history pagination (messages with seq < this)
    this.newestSeq = null; // for delta polling (messages with seq > this)

    // Local conversation cache (array of Conversation)
    this.conversations = [];
    this.hasMoreConversations = false;

    // Poll timers
    this._fastPoll = null;
    this._slowPoll = null;
  }

  static getInstance = () => {
    if (!ChatManager.instance) ChatManager.instance = new ChatManager();
    return ChatManager.instance;
  };

  // Kept for API compatibility; no SDK to initialize anymore.
  static initialize = async () => {
    ChatManager.initialized = true;
    return true;
  };

  // No chat-specific auth token — our JWT (already on the Apollo client) authorizes
  // every request. `arg` is the legacy token, ignored. We resolve myUserId lazily.
  static login = async () => {
    ChatManager.initialized = true;
    ChatManager.authorized = true;
    return { uid: ChatManager.myUserId };
  };

  static logout = async () => {
    const inst = ChatManager.instance;
    if (inst) inst._stopPolling();
    ChatManager.initialized = null;
    ChatManager.authorized = null;
    ChatManager.myUserId = null;
    ChatManager.instance = null;
    return true;
  };

  get initialized() {
    return ChatManager.initialized;
  }

  // ---- Connect: load conversations + start the slow (list/unread) poll ----
  connect = async (settings) => {
    const { onConversationUpdate, onConversationAdd } = settings;
    this.onConversationUpdate = onConversationUpdate;
    this.onConversationAdd = onConversationAdd;

    this.conversations = [];
    const conversations = await this.fetchConversations();

    this._startSlowPoll();
    return conversations;
  };

  // First page (also used by the slow poll to refresh the top of the list).
  fetchConversations = async () => {
    try {
      const { data } = await apollo.query({
        query: getConversationsQuery,
        variables: { limit: CONVERSATION_PAGE_SIZE },
        fetchPolicy: 'network-only',
      });
      const conversations = (data?.conversations || []).map(
        Conversation.createFromApiConversation,
      );
      this.conversations = conversations;
      this.hasMoreConversations = conversations.length === CONVERSATION_PAGE_SIZE;
      return conversations;
    } catch (e) {
      console.log('fetchConversations', e);
      return [];
    }
  };

  // Next page (infinite scroll). Appends to the cache and returns ALL conversations.
  fetchMoreConversations = async () => {
    if (!this.hasMoreConversations || !this.conversations.length) return this.conversations;
    const last = this.conversations[this.conversations.length - 1];
    const cursor = `${last.lastMessageAt || ''}|${last.groupId}`;
    try {
      const { data } = await apollo.query({
        query: getConversationsQuery,
        variables: { limit: CONVERSATION_PAGE_SIZE, cursor },
        fetchPolicy: 'network-only',
      });
      const page = (data?.conversations || []).map(Conversation.createFromApiConversation);
      // Dedupe defensively (cursor is keyset, but guard against races).
      const seen = new Set(this.conversations.map((c) => c.groupId));
      const fresh = page.filter((c) => !seen.has(c.groupId));
      this.conversations = [...this.conversations, ...fresh];
      this.hasMoreConversations = page.length === CONVERSATION_PAGE_SIZE;
      return this.conversations;
    } catch (e) {
      console.log('fetchMoreConversations', e);
      return this.conversations;
    }
  };

  // ---- Open a conversation: load history + start the fast (delta) poll ----
  subscribeToGroup = async (settings) => {
    const { groupId, onMessage } = settings;
    this.onMessage = onMessage;
    this.subscribedGroupId = groupId;
    this.oldestSeq = null;
    this.newestSeq = null;

    const initialMessages = await this._loadHistory(groupId);

    // Mark read on open, then start delta polling.
    this.markConversationRead(groupId);
    this._startFastPoll();

    return initialMessages;
  };

  _loadHistory = async (groupId) => {
    try {
      const { data } = await apollo.query({
        query: getMessagesQuery,
        variables: { conversationUid: groupId, limit: MESSAGE_LIMIT },
        fetchPolicy: 'network-only',
      });
      // API returns newest-first; convert + sort ascending for the UI.
      const messages = (data?.messages || []).map(Message.createFromApiMessage);
      if (messages.length) {
        const seqs = messages.map((m) => Number(m.seq));
        this.oldestSeq = Math.min(...seqs);
        this.newestSeq = Math.max(...seqs);
      }
      return messages.sort((a, b) => a.sentAt - b.sentAt);
    } catch (e) {
      console.log('_loadHistory error', e);
      // Leave newestSeq null so the first delta poll fetches everything as a fallback.
      return [];
    }
  };

  // Older messages for upward pagination (kept name `fetchMessages`).
  fetchMessages = async () => {
    if (!this.subscribedGroupId || this.oldestSeq == null) return [];
    try {
      const { data } = await apollo.query({
        query: getMessagesQuery,
        variables: {
          conversationUid: this.subscribedGroupId,
          limit: MESSAGE_LIMIT,
          beforeCursor: String(this.oldestSeq),
        },
        fetchPolicy: 'network-only',
      });
      const messages = (data?.messages || []).map(Message.createFromApiMessage);
      if (messages.length) {
        this.oldestSeq = Math.min(...messages.map((m) => Number(m.seq)));
      }
      return messages.sort((a, b) => a.sentAt - b.sentAt);
    } catch (e) {
      console.log('fetchMessages', e);
      return [];
    }
  };

  fetchAllUnreadMessageCount = async () => {
    try {
      const { data } = await apollo.query({
        query: getChatUnreadCountQuery,
        // Exclude the currently-open conversation from the total so the sidebar
        // dot doesn't flash on (poll) then off (mark-read).
        variables: { excludeConversationUid: this.subscribedGroupId || undefined },
        fetchPolicy: 'network-only',
      });
      return data?.chatUnreadCount || 0;
    } catch (e) {
      console.log('fetchAllUnreadMessageCount', e);
      return 0;
    }
  };

  // ---- Send ----
  sendTextMessage = async (text, isGroup, overrideReceiver) => {
    const conversationUid = overrideReceiver || this.subscribedGroupId;
    return this._send({ conversationUid, type: 'TEXT', text });
  };

  // file = browser File; messageType/receiverType retained for signature compat.
  sendMediaMessage = async ({ file }) => {
    const conversationUid = this.subscribedGroupId;
    const { url, name } = await this._uploadFile(file);
    let type = 'FILE';
    if (file.type.includes('image')) type = 'IMAGE';
    else if (file.type.includes('video')) type = 'VIDEO';
    return this._send({ conversationUid, type, attachmentUrl: url, attachmentName: name });
  };

  _send = async ({ conversationUid, type, text, attachmentUrl, attachmentName }) => {
    const { data } = await apollo.mutate({
      mutation: sendMessageMutation,
      variables: {
        record: {
          conversationUid,
          clientMsgId: newClientMsgId(),
          type,
          text,
          attachmentUrl,
          attachmentName,
        },
      },
    });
    const message = Message.createFromApiMessage(data.sendMessage);
    if (message.seq != null) this.newestSeq = Number(message.seq);

    // Update local conversation preview + notify.
    const conversation = this._findLocal(conversationUid);
    if (conversation) {
      conversation.lastMessage = message;
      if (this.onConversationUpdate) this.onConversationUpdate(conversation);
    }
    if (this.onMessage) this.onMessage(message);
    return message;
  };

  // Upload via the existing apollo-upload-client `uploadFiles` mutation. Returns { url, name }.
  _uploadFile = async (file) => {
    const { data } = await apollo.mutate({
      mutation: uploadFilesMutation,
      variables: { files: [file] },
    });
    const uploaded = data?.uploadFiles?.[0];
    return { url: uploaded?.url, name: file.name };
  };

  markAsRead = (messageId, groupId) => {
    const conversationUid = groupId || this.subscribedGroupId;
    this.markConversationRead(conversationUid);
  };

  markConversationRead = (conversationUid) => {
    if (!conversationUid) return;
    apollo
      .mutate({
        mutation: markConversationReadMutation,
        variables: { conversationUid },
      })
      .then(() => {
        const conversation = this._findLocal(conversationUid);
        if (conversation) {
          conversation.unreadMessageCount = 0;
          if (this.onConversationUpdate) this.onConversationUpdate(conversation);
        }
      })
      .catch((e) => console.log('markConversationRead', e));
  };

  // ---- Start a 1:1 with another user (coach<->trainee). Returns Conversation. ----
  startConversation = async (withUserUid) => {
    const { data } = await apollo.mutate({
      mutation: startConversationMutation,
      variables: { withUserUid },
    });
    const conversation = Conversation.createFromApiConversation(data.startConversation);
    if (!this._findLocal(conversation.groupId)) {
      this.conversations.push(conversation);
      if (this.onConversationAdd) this.onConversationAdd(conversation);
    }
    return conversation;
  };

  findConversationByGroupId = async (groupId) => {
    return this._findLocal(groupId) || null;
  };

  unsubscribeFromGroup = () => {
    this.subscribedGroupId = null;
    this._stopFastPoll();
  };

  unsubscribeFromMessages = () => {
    this._stopPolling();
    this.onMessage = null;
    this.onConversationUpdate = null;
    this.onConversationAdd = null;
    this.subscribedGroupId = null;
    this.conversations = [];
  };

  // ---- Polling ----
  _startFastPoll = () => {
    this._stopFastPoll();
    this._fastPoll = setInterval(this._pollNewMessages, FAST_POLL_MS);
  };

  _stopFastPoll = () => {
    if (this._fastPoll) clearInterval(this._fastPoll);
    this._fastPoll = null;
  };

  _startSlowPoll = () => {
    this._stopSlowPoll();
    // The web dashboard is a foreground tab while open — send a heartbeat so the
    // user is considered "active" (recipients of their messages get in-app, not push).
    this._heartbeat();
    this._slowPoll = setInterval(this._heartbeat, SLOW_POLL_MS);
    // Conversation list + unread on a fast cadence so the unread badge appears
    // within seconds (not 20s), even when no conversation is open.
    this._unreadPoll = setInterval(this._pollConversations, UNREAD_POLL_MS);
  };

  _heartbeat = () => {
    apollo.mutate({ mutation: chatHeartbeatMutation }).catch(() => {});
  };

  _stopSlowPoll = () => {
    if (this._slowPoll) clearInterval(this._slowPoll);
    this._slowPoll = null;
    if (this._unreadPoll) clearInterval(this._unreadPoll);
    this._unreadPoll = null;
  };

  _stopPolling = () => {
    this._stopFastPoll();
    this._stopSlowPoll();
  };

  _pollNewMessages = async () => {
    if (!this.subscribedGroupId) return;
    try {
      const { data } = await apollo.query({
        query: getNewMessagesQuery,
        variables: {
          conversationUid: this.subscribedGroupId,
          afterCursor: this.newestSeq != null ? String(this.newestSeq) : undefined,
        },
        fetchPolicy: 'network-only',
      });
      const messages = (data?.newMessages || []).map(Message.createFromApiMessage);
      if (!messages.length) return;

      this.newestSeq = Math.max(...messages.map((m) => Number(m.seq)));
      // Only keep-read when this is genuinely the open conversation AND the tab is
      // focused. Otherwise we'd clear the unread the list-poll just set, making the
      // badge flash on then off.
      const conversationUid = this.subscribedGroupId;
      const tabFocused =
        typeof document === 'undefined' || !document.hidden;
      if (conversationUid && tabFocused) {
        this.markConversationRead(conversationUid);
      }

      messages.forEach((message) => {
        if (this.onMessage) this.onMessage(message);
      });
    } catch (e) {
      console.log('_pollNewMessages', e);
    }
  };

  // Slow poll: refresh the TOP page in place and merge into the cache, so any
  // additional pages the user scrolled to load are preserved.
  _pollConversations = async () => {
    try {
      const { data } = await apollo.query({
        query: getConversationsQuery,
        variables: { limit: CONVERSATION_PAGE_SIZE },
        fetchPolicy: 'network-only',
      });
      const page = (data?.conversations || []).map(Conversation.createFromApiConversation);
      // The currently-open conversation is being read live — never show it as
      // unread (avoids the badge flashing on the open chat between polls).
      const openId = this.subscribedGroupId;
      page.forEach((c) => {
        if (openId && c.groupId === openId) c.unreadMessageCount = 0;
      });
      const byId = new Map(this.conversations.map((c) => [c.groupId, c]));
      page.forEach((c) => byId.set(c.groupId, c)); // upsert fresh top page
      this.conversations = Array.from(byId.values());
      page.forEach((conversation) => {
        if (this.onConversationUpdate) this.onConversationUpdate(conversation);
      });
    } catch (e) {
      console.log('_pollConversations', e);
    }
  };

  _findLocal = (groupId) =>
    this.conversations.find((conversation) => conversation.groupId === groupId);
}

// ===========================================================================
// Models — same shapes the UI already consumes.
// ===========================================================================

class User {
  constructor(id, name, avatar) {
    this.id = id;
    this.name = name;
    this.avatar = avatar;
  }
}

class Message {
  constructor(id, groupId, text, type, attachment, sentAt, user, isOwned, extra = {}) {
    this.id = id;
    this.groupId = groupId;
    this.text = text;
    this.type = type;
    this.attachment = attachment;
    this.sentAt = sentAt;
    this.user = user;
    this.isOwned = isOwned;
    this.seq = extra.seq;
    this.clientMsgId = extra.clientMsgId;
    this.customData = extra.customData;
    this.attachmentName = extra.attachmentName;
  }

  static createFromApiMessage = (apiMessage) => {
    const {
      uid,
      conversationUid,
      seq,
      clientMsgId,
      type,
      text,
      attachmentUrl,
      attachmentName,
      customData,
      createdAt,
      sender,
      senderName,
      senderImage,
    } = apiMessage;

    const senderUid = sender?.uid;
    const isOwned = ChatManager.myUserId === senderUid;
    let parsedCustom = null;
    try {
      parsedCustom = customData ? JSON.parse(customData) : null;
    } catch (e) {
      parsedCustom = null;
    }

    return new Message(
      uid,
      conversationUid,
      text,
      TYPE_MAP[type] || 'text',
      attachmentUrl || (parsedCustom && parsedCustom.icon) || null,
      toUnixSeconds(createdAt),
      new User(senderUid, senderName, senderImage),
      isOwned,
      { seq, clientMsgId, customData: parsedCustom, attachmentName },
    );
  };
}

class Conversation {
  constructor(groupId, user, lastMessage = null, unreadMessageCount = 0, isGroup, lastMessageAt, stats = {}) {
    this.groupId = groupId; // = conversation.uid
    this.user = user;
    this.lastMessage = lastMessage;
    this.unreadMessageCount = unreadMessageCount;
    this.isGroup = isGroup;
    // Raw ISO last-activity timestamp (null for empty conversations) — used to
    // build the pagination cursor.
    this.lastMessageAt = lastMessageAt || null;
    // Underlying CoachGroup uid (group conversations) — used to match a group
    // conversation from the program page (which knows the CoachGroup uid).
    this.coachGroupUid = stats.coachGroupUid || null;
    // Info-panel stats / presence.
    this.messageCount = stats.messageCount || 0;
    this.otherMemberPresence = stats.otherMemberPresence || null; // 'online' | 'offline'
    this.otherMemberLastSeenAt = stats.otherMemberLastSeenAt || null;
    this.otherMemberConversationCount = stats.otherMemberConversationCount || 0;
  }

  static createFromApiConversation = (apiConversation) => {
    const {
      uid,
      type,
      name,
      imageUrl,
      lastMessage,
      unreadCount,
      lastMessageAt,
      coach,
      trainee,
      group,
      messageCount,
      otherMemberPresence,
      otherMemberLastSeenAt,
      otherMemberConversationCount,
    } = apiConversation;

    const isGroup = type === 'GROUP';

    let displayUser;
    if (isGroup) {
      displayUser = new User(group?.uid || uid, name || group?.name, imageUrl);
    } else {
      // Direct: show the OTHER participant relative to the current user.
      const coachIsMe = coach?.user?.uid === ChatManager.myUserId;
      const other = coachIsMe ? trainee : coach;
      const otherName = other?.fullName;
      const otherAvatar =
        (other?.profileImage || other?.avatar)?.compressedUrl ||
        (other?.profileImage || other?.avatar)?.url;
      const otherUserUid = other?.user?.uid;
      displayUser = new User(otherUserUid, otherName, otherAvatar);
    }

    const message = lastMessage ? Message.createFromApiMessage(lastMessage) : null;

    return new Conversation(
      uid,
      displayUser,
      message,
      unreadCount || 0,
      isGroup,
      lastMessageAt,
      {
        coachGroupUid: group?.uid || null,
        messageCount,
        otherMemberPresence,
        otherMemberLastSeenAt,
        otherMemberConversationCount,
      },
    );
  };
}

export default ChatManager;
export { Conversation, Message, User };
